import { useEffect, useState, useSyncExternalStore } from "react";
import type { Dict } from "@/lib/i18n";
import { Card, cx } from "@/components/ui";

const DATA_CACHE = "gymlog-data";
const DISMISS_KEY = "gymlog-install-dismissed";

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

// The install prompt fires once, possibly before the card mounts, so keep it at module level.
let deferredPrompt: InstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as InstallPromptEvent;
    promptListeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    promptListeners.forEach((l) => l());
  });
}

/** Registers the service worker (production builds only). */
export function registerServiceWorker() {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});
}

/** Removes cached API answers (they hold the previous user's data): on login and logout. */
export function clearOfflineData() {
  if ("caches" in window) caches.delete(DATA_CACHE).catch(() => {});
}

function subscribeOnline(cb: () => void) {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
}

/** Thin banner shown while the device is offline. */
export function OfflineBanner({ t }: { t: Dict }) {
  const online = useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
  if (online) return null;
  return (
    <div className="sticky top-0 z-30 -mx-4 mb-3 bg-surface-2 px-4 py-2 text-center text-xs text-muted">
      {t.offlineBanner}
    </div>
  );
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

/**
 * "Install the app" card. Uses the browser's install prompt where there is one (Android, desktop
 * Chrome/Edge) and shows the Share → Add to Home Screen steps on iPhone. Hidden once installed.
 * With `dismissible`, "Later" hides it on this device.
 */
export function InstallCard({ t, dismissible = false }: { t: Dict; dismissible?: boolean }) {
  const [state, setState] = useState<"hidden" | "prompt" | "ios" | "manual" | "installed">("hidden");

  useEffect(() => {
    const update = () => {
      if (isStandalone()) return setState(dismissible ? "hidden" : "installed");
      if (dismissible) {
        try {
          if (localStorage.getItem(DISMISS_KEY)) return setState("hidden");
        } catch {}
      }
      if (deferredPrompt) return setState("prompt");
      if (isIos()) return setState("ios");
      setState(dismissible ? "hidden" : "manual");
    };
    update();
    promptListeners.add(update);
    return () => {
      promptListeners.delete(update);
    };
  }, [dismissible]);

  if (state === "hidden") return null;

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice.catch(() => null);
    deferredPrompt = null;
    setState(isStandalone() ? "installed" : dismissible ? "hidden" : "manual");
  }

  function later() {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setState("hidden");
  }

  return (
    <Card className={cx("flex items-start gap-3", dismissible && "mb-4")}>
      <img src="/icons/icon-192.png" alt="" className="h-11 w-11 shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-semibold">{state === "installed" ? t.installed : t.installApp}</p>
        {state !== "installed" && <p className="text-sm text-muted">{t.installAppHint}</p>}
        {state === "ios" && <p className="text-sm">{t.installIosSteps}</p>}
        {state === "manual" && <p className="text-sm">{t.installManual}</p>}
        {(state === "prompt" || (dismissible && state === "ios")) && (
          <div className="flex gap-2 pt-1">
            {state === "prompt" && (
              <button
                type="button"
                onClick={install}
                className="h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-fg"
              >
                {t.installNow}
              </button>
            )}
            {dismissible && (
              <button type="button" onClick={later} className="h-10 rounded-xl px-3 text-sm text-muted">
                {t.later}
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
